// T1.1 (migratie 030) — vier stadia in plaats van acht. Verloren en Afgerond
// zijn geen stadium meer maar een archiefvlag op `projects.status`; zie
// statusConfig hieronder. Dit bestand is de enige bron: ProjectModal,
// de projectlijst en de projectpagina importeren hiervandaan.

export type ProjectStage = 'verkennen' | 'voorstel' | 'gewonnen' | 'operatie';
export type ProjectStatus = 'actief' | 'verloren' | 'afgerond';

type BadgeConfig = {
  label: string;
  bg: string;
  text: string;
  border: string;
};

export const stageConfig: Record<string, BadgeConfig> = {
  verkennen: {
    label: 'Verkennen',
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-800 dark:text-blue-300',
    border: 'border-l-blue-500',
  },
  voorstel: {
    label: 'Voorstel',
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-800 dark:text-purple-300',
    border: 'border-l-purple-500',
  },
  gewonnen: {
    label: 'Gewonnen',
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-800 dark:text-green-300',
    border: 'border-l-green-500',
  },
  operatie: {
    label: 'Operatie',
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-800 dark:text-emerald-300',
    border: 'border-l-emerald-500',
  },
};

/** De vier stadia in trajectvolgorde. Gebruik dit voor elke picker of pipeline. */
export const PROJECT_STAGES: ProjectStage[] = ['verkennen', 'voorstel', 'gewonnen', 'operatie'];

export const statusConfig: Record<string, BadgeConfig> = {
  actief: {
    label: 'Actief',
    bg: 'bg-sky-100 dark:bg-sky-900/30',
    text: 'text-sky-800 dark:text-sky-300',
    border: 'border-l-sky-500',
  },
  verloren: {
    label: 'Verloren',
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-800 dark:text-red-300',
    border: 'border-l-red-500',
  },
  afgerond: {
    label: 'Afgerond',
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-800 dark:text-gray-300',
    border: 'border-l-gray-500',
  },
};

export const PROJECT_STATUSES: ProjectStatus[] = ['actief', 'verloren', 'afgerond'];

/** Een traject is afgesloten zodra het niet meer actief is. */
export const isAfgeslotenStatus = (status: string | null | undefined): boolean =>
  status === 'verloren' || status === 'afgerond';

const ONBEKEND: Omit<BadgeConfig, 'label'> = {
  bg: 'bg-gray-100 dark:bg-gray-800',
  text: 'text-gray-800 dark:text-gray-300',
  border: 'border-l-gray-500',
};

export const getStageConfig = (stage: string | null | undefined): BadgeConfig => {
  return stageConfig[stage ?? ''] || { label: stage || 'Onbekend', ...ONBEKEND };
};

export const getStatusConfig = (status: string | null | undefined): BadgeConfig => {
  return statusConfig[status ?? ''] || { label: status || 'Onbekend', ...ONBEKEND };
};

export const formatAmount = (amount: number | null) => {
  if (!amount) return '-';
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const getInitials = (name: string | null | undefined): string => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};
